import React, { useCallback, useEffect, useReducer, useState } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { Route, Switch, withRouter } from "react-router-dom";
import esLocale from "date-fns/locale/es";
import DateFnsUtils from "@date-io/date-fns";
import { esES } from "@material-ui/core/locale";
import { createMuiTheme, ThemeProvider } from "@material-ui/core/styles";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import CssBaseline from "@material-ui/core/CssBaseline";
import { Provider } from "../context";
import { TEMPLATES } from "../constants";
import { storeByHostname, storeBySlug } from "../graphql/queries";
import logger from "../logger";
import Home from "../pages/Home";
import Payment from "../pages/Payment";
import Receipt from "../pages/Receipt";
import VerifyPayment from "../pages/VerifyPayment";
import WireTransfer from "../pages/WireTransfer";
import ErrorBoundary from "./ErrorBoundary";
import Loading from "./Loading";

const theme = createMuiTheme(
  {
    palette: {
      primary: {
        main: "#470A68"
      },
      secondary: {
        main: "#FF8F1C"
      }
    }
  },
  esES
);

const App = ({ history, hostname, store_slug }) => {
  const [redirect, setRedirect] = useState(null);

  const initialState = {
    fetchingStore: true,
    fetchingComunas: true,
    comunas: [],
    store: null,
    history,
    useStoreSlug: Boolean(store_slug)
  };

  const reducer = (state, { type, payload }) => {
    switch (type) {
      case "fetchingStore":
      case "comunas":
        return { ...state, [type]: payload };
      case "store":
        return {
          ...state,
          store: payload,
          template:
            ((((payload || {}).body || {}).config || {}).templates || {}).shop || TEMPLATES.default
        };
      default:
        return { ...state };
    }
  };

  const [context, dispatch] = useReducer(reducer, initialState);

  const fetchStore = useCallback(async () => {
    dispatch({ type: "fetchingStore", payload: true });

    try {
      const vars = {};
      let query = null;
      let action = null;

      if (store_slug) {
        vars.slug = store_slug;
        query = storeBySlug;
        action = "storeBySlug";
      }

      if (hostname) {
        vars.hostname = hostname;
        query = storeByHostname;
        action = "storeByHostname";
      }

      if (query) {
        const result = await API.graphql(graphqlOperation(query, vars));

        if (result.data[action].items.length > 0) {
          const body = JSON.parse(result.data[action].items[0].body);

          const store = {
            ...result.data[action].items[0],
            body
          };

          if (store.status < 1) setRedirect("/disabled");
          else dispatch({ type: "store", payload: store });
        } else {
          setRedirect("/404");
          logger("App.fetchStore.404", result);
        }
      }
    } catch (err) {
      logger("App.fetchStore.error", err);

      if (err.errors && err.errors.length > 0) {
        const error = err.errors[0];

        if (error && error.message && error.message.includes("400")) {
          setRedirect("/400");
        }
      }

      setRedirect("/500");
    }

    dispatch({ type: "fetchingStore", payload: false });
  }, [store_slug, hostname]);

  const fetchComunas = useCallback(async () => {
    dispatch({ type: "fetchingComunas", payload: true });

    try {
      const result = await fetch(`${process.env.REACT_APP_URL_UTILS}/comunas`);

      if (result.status === 200) {
        const comunas = await result.json();
        dispatch({ type: "comunas", payload: comunas });
      }
    } catch (err) {
      logger("App.fetchingComunas.error", err);
      setRedirect("/500");
    }

    dispatch({ type: "fetchingComunas", payload: false });
  }, []);

  useEffect(() => {
    if (store_slug || hostname) {
      fetchStore();
      fetchComunas();
    }
  }, [store_slug, hostname, fetchStore, fetchComunas]);

  useEffect(() => {
    if (context.store) {
      document.title = `${context.store.name} - Carrito Fácil`;
    }
  }, [context.store]);

  useEffect(() => {
    if (redirect) {
      history.push(redirect);
    }
  }, [redirect, history]);

  if (context.fetchingStore) return <Loading fullscreen />;

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <Provider value={context}>
          <CssBaseline />
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={esLocale}>
            <div className="app-container">
              <Switch>
                <Route
                  exact
                  path={`${store_slug ? "/:store_slug" : ""}/pago/:cartID`}
                  component={({ match }) => <Payment cartID={match.params.cartID} />}
                />
                <Route
                  exact
                  path={`${store_slug ? "/:store_slug" : ""}/transferencia/:cartID`}
                  component={({ match }) => <WireTransfer cartID={match.params.cartID} />}
                />
                <Route
                  exact
                  path={`${store_slug ? "/:store_slug" : ""}/verificacion/:cartID`}
                  component={({ match }) => <VerifyPayment cartID={match.params.cartID} />}
                />
                <Route
                  exact
                  path={`${store_slug ? "/:store_slug" : ""}/comprobante/:cartID`}
                  component={({ match }) => <Receipt cartID={match.params.cartID} />}
                />
                <Route
                  exact
                  path={`${store_slug ? "/:store_slug" : ""}/:params?`}
                  component={Home}
                />
              </Switch>
            </div>
          </MuiPickersUtilsProvider>
        </Provider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default withRouter(App);
